import { sql } from "drizzle-orm";
import { pgTable, text, varchar, integer, boolean, timestamp, jsonb, index } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import type { z } from "zod";

import { tenants } from './common';

export const safetyIncidents = pgTable("safety_incidents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  referenceNumber: text("reference_number").notNull().unique(),
  title: text("title").notNull(),
  description: text("description").notNull(),
  incidentType: text("incident_type").notNull(),
  severityLevel: text("severity_level").notNull(),
  status: text("status").notNull().default("draft"),

  projectId: text("project_id"),
  siteId: text("site_id"),
  areaId: text("area_id"),
  subArea: text("sub_area"),
  gateId: text("gate_id"),
  gpsCoordinates: text("gps_coordinates"),
  locationDescription: text("location_description"),

  timeOfIncident: text("time_of_incident"),
  reportedTime: text("reported_time"),
  reportedBy: text("reported_by").notNull(),
  reportedRole: text("reported_role").notNull(),
  reportedCompanyId: text("reported_company_id"),

  linkedWorkers: text("linked_workers").array().default([]),
  linkedCompanies: text("linked_companies").array().default([]),
  linkedPTW: text("linked_ptw"),
  linkedPermitId: text("linked_permit_id"),
  linkedEquipment: text("linked_equipment").array().default([]),
  involvedWorkerIds: text("involved_worker_ids").array().default([]),
  witnessWorkerIds: text("witness_worker_ids").array().default([]),
  witnessNames: text("witness_names"),

  injuriesCount: integer("injuries_count").default(0),
  fatalitiesCount: integer("fatalities_count").default(0),
  nearMiss: boolean("near_miss").notNull().default(false),
  environmentalImpact: boolean("environmental_impact").notNull().default(false),
  propertyDamage: boolean("property_damage").notNull().default(false),
  estimatedCost: text("estimated_cost"),

  initialResponse: text("initial_response"),
  immediateActions: jsonb("immediate_actions").$type<{ description: string; takenBy: string; timestamp: string }[]>().default([]),

  rootCause: text("root_cause"),
  correctiveActions: jsonb("corrective_actions").$type<{
    id: string;
    actionDescription: string;
    assignedTo: string;
    dueDate: string;
    completionStatus: string;
    verifiedBy?: string;
  }[]>().default([]),
  preventiveActions: text("preventive_actions").array().default([]),
  investigationLead: text("investigation_lead"),

  escalationLevel: text("escalation_level").notNull().default("none"),
  lockdownTriggered: boolean("lockdown_triggered").notNull().default(false),
  regulatoryReportable: boolean("regulatory_reportable").notNull().default(false),
  notifiedAuthorities: text("notified_authorities").array().default([]),
  insuranceClaim: boolean("insurance_claim").notNull().default(false),

  riddorReportable: boolean("riddor_reportable").notNull().default(false),
  riddorClassification: text("riddor_classification"),

  attachments: jsonb("attachments").$type<{ name: string; fileId: string; type: string; uploadedBy?: string; uploadedAt?: string }[]>().default([]),
  metadata: jsonb("metadata").$type<Record<string, any>>().default({}),
  tags: text("tags").array().default([]),

  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  closedDate: text("closed_date"),
  closedBy: text("closed_by"),
}, (t) => ({
  idxIncidentsTenantStatus: index("idx_incidents_tenant_status").on(t.tenantId, t.status),
  idxIncidentsTenantCreatedAt: index("idx_incidents_tenant_created_at").on(t.tenantId, t.createdAt),
  idxIncidentsTenantSeverity: index("idx_incidents_tenant_severity").on(t.tenantId, t.severityLevel),
}));
export const insertSafetyIncidentSchema = createInsertSchema(safetyIncidents).omit({ id: true, tenantId: true });
export type InsertSafetyIncident = z.infer<typeof insertSafetyIncidentSchema>;
export type SafetyIncident = typeof safetyIncidents.$inferSelect;
export const incidents = safetyIncidents;
export const insertIncidentSchema = createInsertSchema(safetyIncidents).omit({ tenantId: true });
export type InsertIncident = z.infer<typeof insertIncidentSchema>;
export type Incident = SafetyIncident;

// ─── Non-Conformances ─────────────────────────────────────────────────
export const nonConformances = pgTable("non_conformances", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  referenceNumber: text("reference_number").notNull().unique(),
  title: text("title").notNull(),
  description: text("description").notNull(),
  category: text("category").notNull(),
  source: text("source").notNull(),
  status: text("status").notNull().default("draft"),
  severityLevel: text("severity_level").notNull().default("minor"),

  projectId: text("project_id"),
  siteId: text("site_id"),
  areaId: text("area_id"),

  detectedAt: text("detected_at"),
  detectedBy: text("detected_by").notNull(),
  detectedRole: text("detected_role"),
  detectedCompanyId: text("detected_company_id"),

  linkedWorkers: text("linked_workers").array().default([]),
  linkedCompanies: text("linked_companies").array().default([]),
  linkedPTW: text("linked_ptw"),
  linkedIncidentId: text("linked_incident_id"),
  linkedAuditRef: text("linked_audit_ref"),
  linkedExternalInspectionRef: text("linked_external_inspection_ref"),

  breachedRequirement: text("breached_requirement"),
  breachedPolicyRef: text("breached_policy_ref"),
  breachedProcedureRef: text("breached_procedure_ref"),

  immediateActionTaken: text("immediate_action_taken"),

  attachments: jsonb("attachments").$type<{ name: string; fileId: string; type: string; uploadedBy?: string; uploadedAt?: string }[]>().default([]),
  metadata: jsonb("metadata").$type<Record<string, any>>().default({}),
  tags: text("tags").array().default([]),

  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  closedDate: text("closed_date"),
  closedBy: text("closed_by"),
});
export const insertNonConformanceSchema = createInsertSchema(nonConformances).omit({ id: true, tenantId: true });
export type InsertNonConformance = z.infer<typeof insertNonConformanceSchema>;
export type NonConformance = typeof nonConformances.$inferSelect;

// ─── Safety Cases ─────────────────────────────────────────────────────
export const safetyCases = pgTable("safety_cases", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  referenceNumber: text("reference_number").notNull().unique(),
  title: text("title").notNull(),
  description: text("description"),
  status: text("status").notNull().default("draft"),
  priority: text("priority").notNull().default("medium"),
  classification: text("classification"),

  projectId: text("project_id"),
  siteId: text("site_id"),
  areaId: text("area_id"),

  caseOwnerId: text("case_owner_id"),
  caseOwnerRole: text("case_owner_role"),
  assignedTeam: text("assigned_team").array().default([]),

  triageCompletedDate: text("triage_completed_date"),
  investigationRequiredFlag: boolean("investigation_required_flag").notNull().default(false),
  containmentRequiredFlag: boolean("containment_required_flag").notNull().default(false),

  findingsSummary: text("findings_summary"),
  lessonsLearned: text("lessons_learned"),

  closureApprovedBy: text("closure_approved_by"),
  closureDate: text("closure_date"),
  closureNotes: text("closure_notes"),
  reopenedDate: text("reopened_date"),
  reopenReason: text("reopen_reason"),

  metadata: jsonb("metadata").$type<Record<string, any>>().default({}),
  tags: text("tags").array().default([]),

  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertSafetyCaseSchema = createInsertSchema(safetyCases).omit({ id: true, tenantId: true });
export type InsertSafetyCase = z.infer<typeof insertSafetyCaseSchema>;
export type SafetyCase = typeof safetyCases.$inferSelect;

// ─── Safety Case Links ────────────────────────────────────────────────
export const safetyCaseLinks = pgTable("safety_case_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  safetyCaseId: text("safety_case_id").notNull(),
  sourceType: text("source_type").notNull(),
  sourceId: text("source_id").notNull(),
  linkNotes: text("link_notes"),
  linkedBy: text("linked_by"),
  linkedDate: text("linked_date"),
});
export const insertSafetyCaseLinkSchema = createInsertSchema(safetyCaseLinks).omit({ id: true, tenantId: true });
export type InsertSafetyCaseLink = z.infer<typeof insertSafetyCaseLinkSchema>;
export type SafetyCaseLink = typeof safetyCaseLinks.$inferSelect;

// ─── Safety Investigations ────────────────────────────────────────────
export const safetyInvestigations = pgTable("safety_investigations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  referenceNumber: text("reference_number").notNull().unique(),
  safetyCaseId: text("safety_case_id").notNull(),
  investigationType: text("investigation_type").notNull(),
  methodology: text("methodology").notNull(),
  status: text("status").notNull().default("draft"),

  leadInvestigatorId: text("lead_investigator_id"),
  leadInvestigatorRole: text("lead_investigator_role"),
  investigationTeam: text("investigation_team").array().default([]),

  startedDate: text("started_date"),
  targetCompletionDate: text("target_completion_date"),
  completedDate: text("completed_date"),

  immediateCauses: text("immediate_causes").array().default([]),
  underlyingCauses: text("underlying_causes").array().default([]),
  systemicCauses: text("systemic_causes").array().default([]),

  humanFactors: text("human_factors").array().default([]),
  trainingFactors: text("training_factors").array().default([]),
  supervisionFactors: text("supervision_factors").array().default([]),
  processFailures: text("process_failures").array().default([]),
  accessFailures: text("access_failures").array().default([]),
  ptwFailures: text("ptw_failures").array().default([]),
  companyGovernanceFailures: text("company_governance_failures").array().default([]),
  equipmentFactors: text("equipment_factors").array().default([]),

  findingsSummary: text("findings_summary"),
  recommendations: text("recommendations").array().default([]),

  attachments: jsonb("attachments").$type<{ name: string; fileId: string; type: string; uploadedBy?: string; uploadedAt?: string }[]>().default([]),
  metadata: jsonb("metadata").$type<Record<string, any>>().default({}),

  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertSafetyInvestigationSchema = createInsertSchema(safetyInvestigations).omit({ id: true, tenantId: true });
export type InsertSafetyInvestigation = z.infer<typeof insertSafetyInvestigationSchema>;
export type SafetyInvestigation = typeof safetyInvestigations.$inferSelect;

// ─── Safety Witness Statements ────────────────────────────────────────
export const safetyWitnessStatements = pgTable("safety_witness_statements", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  investigationId: text("investigation_id").notNull(),
  witnessName: text("witness_name").notNull(),
  witnessRole: text("witness_role"),
  witnessCompanyId: text("witness_company_id"),
  witnessWorkerId: text("witness_worker_id"),

  statementText: text("statement_text").notNull(),
  statementDate: text("statement_date").notNull(),
  interviewedBy: text("interviewed_by").notNull(),
  interviewLocation: text("interview_location"),
  interviewNotes: text("interview_notes"),

  confidential: boolean("confidential").notNull().default(false),
  consentGiven: boolean("consent_given").notNull().default(true),

  attachments: jsonb("attachments").$type<{ name: string; fileId: string; type: string; uploadedBy?: string; uploadedAt?: string }[]>().default([]),

  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertSafetyWitnessStatementSchema = createInsertSchema(safetyWitnessStatements).omit({ id: true, tenantId: true });
export type InsertSafetyWitnessStatement = z.infer<typeof insertSafetyWitnessStatementSchema>;
export type SafetyWitnessStatement = typeof safetyWitnessStatements.$inferSelect;

// ─── Safety Actions (CAPA) ────────────────────────────────────────────
export const safetyActions = pgTable("safety_actions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  referenceNumber: text("reference_number").notNull().unique(),
  safetyCaseId: text("safety_case_id"),
  investigationId: text("investigation_id"),
  actionType: text("action_type").notNull(),
  domain: text("domain").notNull(),
  title: text("title").notNull(),
  description: text("description").notNull(),
  status: text("status").notNull().default("open"),
  priority: text("priority").notNull().default("medium"),

  projectId: text("project_id"),
  siteId: text("site_id"),
  areaId: text("area_id"),

  responsiblePersonId: text("responsible_person_id"),
  responsibleRole: text("responsible_role"),
  responsibleCompanyId: text("responsible_company_id"),
  verifierId: text("verifier_id"),
  approverId: text("approver_id"),

  dueDate: text("due_date"),
  startedDate: text("started_date"),
  completedDate: text("completed_date"),
  verifiedDate: text("verified_date"),
  closedDate: text("closed_date"),

  completionNotes: text("completion_notes"),
  verificationNotes: text("verification_notes"),
  rejectionReason: text("rejection_reason"),
  cancellationReason: text("cancellation_reason"),

  attachments: jsonb("attachments").$type<{ name: string; fileId: string; type: string; uploadedBy?: string; uploadedAt?: string }[]>().default([]),
  metadata: jsonb("metadata").$type<Record<string, any>>().default({}),
  tags: text("tags").array().default([]),

  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertSafetyActionSchema = createInsertSchema(safetyActions).omit({ id: true, tenantId: true });
export type InsertSafetyAction = z.infer<typeof insertSafetyActionSchema>;
export type SafetyAction = typeof safetyActions.$inferSelect;

// ─── Safety Action Evidence ───────────────────────────────────────────
export const safetyActionEvidence = pgTable("safety_action_evidence", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  actionId: text("action_id").notNull(),
  evidenceType: text("evidence_type").notNull(),
  title: text("title").notNull(),
  description: text("description"),
  fileId: text("file_id"),
  fileName: text("file_name"),
  fileType: text("file_type"),
  uploadedBy: text("uploaded_by").notNull(),
  uploadedDate: text("uploaded_date").notNull(),
  verifiedBy: text("verified_by"),
  verifiedDate: text("verified_date"),
  metadata: jsonb("metadata").$type<Record<string, any>>().default({}),
});
export const insertSafetyActionEvidenceSchema = createInsertSchema(safetyActionEvidence).omit({ id: true, tenantId: true });
export type InsertSafetyActionEvidence = z.infer<typeof insertSafetyActionEvidenceSchema>;
export type SafetyActionEvidence = typeof safetyActionEvidence.$inferSelect;

// ─── Safety Effectiveness Reviews ─────────────────────────────────────
export const safetyEffectivenessReviews = pgTable("safety_effectiveness_reviews", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  safetyCaseId: text("safety_case_id").notNull(),
  actionId: text("action_id"),
  reviewResult: text("review_result").notNull(),
  reviewNotes: text("review_notes"),

  scheduledDate: text("scheduled_date"),
  completedDate: text("completed_date"),
  reviewedBy: text("reviewed_by").notNull(),
  reviewedRole: text("reviewed_role"),

  recurrenceDetected: boolean("recurrence_detected").notNull().default(false),
  recurrenceDetails: text("recurrence_details"),
  followUpRequired: boolean("follow_up_required").notNull().default(false),
  followUpActionId: text("follow_up_action_id"),

  attachments: jsonb("attachments").$type<{ name: string; fileId: string; type: string; uploadedBy?: string; uploadedAt?: string }[]>().default([]),
  metadata: jsonb("metadata").$type<Record<string, any>>().default({}),

  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertSafetyEffectivenessReviewSchema = createInsertSchema(safetyEffectivenessReviews).omit({ id: true, tenantId: true });
export type InsertSafetyEffectivenessReview = z.infer<typeof insertSafetyEffectivenessReviewSchema>;
export type SafetyEffectivenessReview = typeof safetyEffectivenessReviews.$inferSelect;

// ─── Safety Containment Records ───────────────────────────────────────
export const safetyContainmentRecords = pgTable("safety_containment_records", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  safetyCaseId: text("safety_case_id").notNull(),
  containmentType: text("containment_type").notNull(),
  status: text("status").notNull().default("active"),
  description: text("description").notNull(),

  projectId: text("project_id"),
  siteId: text("site_id"),
  areaId: text("area_id"),
  gateId: text("gate_id"),

  affectedWorkerId: text("affected_worker_id"),
  affectedCompanyId: text("affected_company_id"),
  affectedEquipmentRef: text("affected_equipment_ref"),
  affectedPtwId: text("affected_ptw_id"),

  implementedBy: text("implemented_by").notNull(),
  implementedRole: text("implemented_role"),
  implementedDate: text("implemented_date").notNull(),

  liftedBy: text("lifted_by"),
  liftedDate: text("lifted_date"),
  liftedReason: text("lifted_reason"),
  replacedByPermanentControlRef: text("replaced_by_permanent_control_ref"),

  metadata: jsonb("metadata").$type<Record<string, any>>().default({}),

  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertSafetyContainmentRecordSchema = createInsertSchema(safetyContainmentRecords).omit({ id: true, tenantId: true });
export type InsertSafetyContainmentRecord = z.infer<typeof insertSafetyContainmentRecordSchema>;
export type SafetyContainmentRecord = typeof safetyContainmentRecords.$inferSelect;

// ─── Safety Audit Events ──────────────────────────────────────────────
export const safetyAuditEvents = pgTable("safety_audit_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  entityType: text("entity_type").notNull(),
  entityId: text("entity_id").notNull(),
  eventType: text("event_type").notNull(),
  actor: text("actor").notNull(),
  actorRole: text("actor_role"),
  timestamp: text("timestamp").notNull(),

  oldValues: jsonb("old_values").$type<Record<string, any>>(),
  newValues: jsonb("new_values").$type<Record<string, any>>(),
  reason: text("reason"),

  projectId: text("project_id"),
  siteId: text("site_id"),
  areaId: text("area_id"),

  evidenceRef: text("evidence_ref"),
  metadata: jsonb("metadata").$type<Record<string, any>>().default({}),
});
export const insertSafetyAuditEventSchema = createInsertSchema(safetyAuditEvents).omit({ id: true, tenantId: true });
export type InsertSafetyAuditEvent = z.infer<typeof insertSafetyAuditEventSchema>;
export type SafetyAuditEvent = typeof safetyAuditEvents.$inferSelect;

// ─── Safety Classification Config ─────────────────────────────────────
export const safetyClassificationConfig = pgTable("safety_classification_config", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  configType: text("config_type").notNull(),
  code: text("code").notNull(),
  label: text("label").notNull(),
  description: text("description"),
  parentCode: text("parent_code"),
  sortOrder: integer("sort_order").notNull().default(0),
  isActive: boolean("is_active").notNull().default(true),

  projectId: text("project_id"),
  siteId: text("site_id"),

  severityWeight: integer("severity_weight"),
  likelihoodWeight: integer("likelihood_weight"),
  riskMatrix: jsonb("risk_matrix").$type<Record<string, any>>(),

  metadata: jsonb("metadata").$type<Record<string, any>>().default({}),

  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertSafetyClassificationConfigSchema = createInsertSchema(safetyClassificationConfig).omit({ id: true, tenantId: true });
export type InsertSafetyClassificationConfig = z.infer<typeof insertSafetyClassificationConfigSchema>;
export type SafetyClassificationConfig = typeof safetyClassificationConfig.$inferSelect;

// ─── Safety Escalation Rules ──────────────────────────────────────────
export const safetyEscalationRules = pgTable("safety_escalation_rules", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  name: text("name").notNull(),
  description: text("description"),
  eventType: text("event_type").notNull(),
  severityLevel: text("severity_level").notNull(),
  escalationTarget: text("escalation_target").notNull(),
  escalationAction: text("escalation_action").notNull(),
  timeThresholdMinutes: integer("time_threshold_minutes"),
  autoEscalate: boolean("auto_escalate").notNull().default(false),
  isActive: boolean("is_active").notNull().default(true),

  projectId: text("project_id"),
  siteId: text("site_id"),

  notificationChannels: text("notification_channels").array().default([]),
  notificationTemplate: text("notification_template"),

  metadata: jsonb("metadata").$type<Record<string, any>>().default({}),

  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export type SafetyEscalationRule = typeof safetyEscalationRules.$inferSelect;
export const observations = pgTable("observations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  title: text("title").notNull(),
  description: text("description").notNull(),
  observationType: text("observation_type").notNull(),
  category: text("category"),
  projectId: text("project_id"),
  siteId: text("site_id"),
  areaId: text("area_id"),
  companyId: text("company_id"),
  gateId: text("gate_id"),
  shiftId: text("shift_id"),
  reportedBy: text("reported_by").notNull(),
  reportedByName: text("reported_by_name"),
  reportedRole: text("reported_role"),
  linkedWorkers: text("linked_workers").array().default([]),
  observedWorkerIds: text("observed_worker_ids").array().default([]),
  witnessWorkerIds: text("witness_worker_ids").array().default([]),
  linkedPtwId: text("linked_ptw_id"),
  photoEvidence: jsonb("photo_evidence").$type<{
    id: string;
    url: string;
    caption?: string;
    uploadedAt: string;
  }[]>().default([]),
  riskLevel: text("risk_level").default("low"),
  immediateActionTaken: text("immediate_action_taken"),
  recommendedActions: text("recommended_actions"),
  status: text("status").notNull().default("open"),
  escalatedToIncidentId: text("escalated_to_incident_id"),
  escalatedToNcId: text("escalated_to_nc_id"),
  observedAt: text("observed_at"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertObservationSchema = createInsertSchema(observations).omit({ id: true, tenantId: true });
export type InsertObservation = z.infer<typeof insertObservationSchema>;
export type Observation = typeof observations.$inferSelect;
export const triageRecords = pgTable("triage_records", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  sourceType: text("source_type").notNull(),
  sourceId: text("source_id").notNull(),
  severityRating: text("severity_rating"),
  classificationOutcome: text("classification_outcome"),
  escalationDecision: text("escalation_decision"),
  caseOwnerId: text("case_owner_id"),
  caseOwnerName: text("case_owner_name"),
  triageNotes: text("triage_notes"),
  triagedBy: text("triaged_by"),
  triagedByName: text("triaged_by_name"),
  status: text("status").notNull().default("pending"),
  triagedAt: text("triaged_at"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertTriageRecordSchema = createInsertSchema(triageRecords).omit({ id: true, tenantId: true });
export type InsertTriageRecord = z.infer<typeof insertTriageRecordSchema>;
export type TriageRecord = typeof triageRecords.$inferSelect;
